import { useEffect, useRef } from 'react';
import { cn } from '@/lib/cn';
import { ALL_ICON, iconForCategory } from '@/lib/categoryIcons';
import FlagBadge from '@/components/FlagBadge';
import type { Category } from '@shared/schemas';

interface Props {
  categories: Category[];
  activeSlug: string | null;
  onChange: (slug: string | null) => void;
}

// Instagram "stories"-style category nav: a horizontal row of uniform icon
// circles. Active category is a filled ink circle; the rest are light.
export default function CategoryStories({ categories, activeSlug, onChange }: Props) {
  const items = [
    { slug: null as string | null, name: 'Vše', Icon: ALL_ICON, flag: null as string | null },
    ...categories.map((c) => ({
      slug: c.slug,
      name: c.name,
      Icon: iconForCategory(c),
      flag: c.flag,
    })),
  ];

  const scrollerRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<HTMLButtonElement>(null);
  const isFirstRunRef = useRef(true);
  const fromChipTapRef = useRef(false);

  // Centre the active chip horizontally (no vertical scroll, unlike
  // scrollIntoView) whenever activeSlug changes from outside this row — a
  // refresh restoring it from the URL, or a category picked elsewhere on the
  // page (a section's "Vše →") that may well be off-screen here, leaving the
  // selection invisible. We deliberately skip it when the change came from
  // tapping a chip in this row itself (fromChipTapRef): the user just tapped
  // what they can already see, so re-centring would yank the row under their
  // finger for no reason.
  useEffect(() => {
    if (categories.length === 0) return; // wait until the row has its chips
    if (fromChipTapRef.current) {
      fromChipTapRef.current = false;
      return;
    }
    if (!activeSlug) return; // "Vše" is leftmost — nothing to centre
    const scroller = scrollerRef.current;
    const active = activeRef.current;
    if (!scroller || !active) return;
    const offset = active.getBoundingClientRect().left - scroller.getBoundingClientRect().left;
    const target = scroller.scrollLeft + offset - (scroller.clientWidth - active.clientWidth) / 2;
    scroller.scrollTo({
      left: Math.max(0, target),
      behavior: isFirstRunRef.current ? 'auto' : 'smooth',
    });
    isFirstRunRef.current = false;
  }, [activeSlug, categories.length]);

  function handleChipClick(slug: string | null) {
    fromChipTapRef.current = true;
    onChange(slug === activeSlug ? null : slug);
  }

  return (
    <div ref={scrollerRef} className="-mx-5 mt-4 flex gap-4 overflow-x-auto px-5 pb-1">
      {items.map(({ slug, name, Icon, flag }) => {
        const active = slug === activeSlug;
        return (
          <button
            key={slug ?? '__all'}
            ref={active ? activeRef : undefined}
            type="button"
            onClick={() => handleChipClick(slug)}
            className="flex w-14 shrink-0 flex-col items-center gap-1.5"
          >
            {/* The badge hangs 1px past the circle, so it needs its own
                positioning context that doesn't move with the active state. */}
            <span className="relative h-14 w-14">
              <span
                className={cn(
                  'flex h-full w-full items-center justify-center rounded-full transition-colors',
                  active ? 'bg-accent text-white' : 'bg-accent-soft text-text',
                )}
              >
                <Icon size={22} />
              </span>
              <FlagBadge flag={flag} />
            </span>
            <span
              className={cn(
                'w-full truncate text-center text-[11px]',
                active ? 'font-bold text-text' : 'font-medium text-text-muted',
              )}
            >
              {name}
            </span>
          </button>
        );
      })}
    </div>
  );
}
